import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  Alert02Icon,
  Tick02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  usePopoverContext
} from "shared/components/Popover";
import type { EmailVariable } from "views/settings-communication/api/types";
import type { ContactUnitLinks } from "../api/crm";
import type { LinkType } from "./link-variable-utils";
import { shortUrlForUnit } from "./email-link-utils";

interface LinkUnitResolverProps {
  // Link variable keys present in the email body.
  linkKeys: LinkType[];
  variables: EmailVariable[];
  units: ContactUnitLinks[];
  selections: Record<LinkType, ContactUnitLinks | undefined>;
  onSelect: (key: LinkType, unit: ContactUnitLinks) => void;
}

const labelFor = (variables: EmailVariable[], key: LinkType): string =>
  variables.find((v) => v.key === key)?.label ?? key;

interface UnitPickerProps {
  linkKey: LinkType;
  units: ContactUnitLinks[];
  selected: ContactUnitLinks | undefined;
  onSelect: (key: LinkType, unit: ContactUnitLinks) => void;
}

const UnitPickerMenu = ({
  linkKey,
  units,
  selected,
  onSelect
}: UnitPickerProps) => {
  const { setOpen } = usePopoverContext();
  if (units.length === 0)
    return (
      <div className="px-4 py-3 text-sm font-medium text-content-300">
        No units available for this contact
      </div>
    );
  return (
    <>
      {units.map((unit) => {
        const url = shortUrlForUnit(unit, linkKey);
        const isCurrent = selected?.unit_id === unit.unit_id;
        return (
          <button
            key={unit.unit_id}
            type="button"
            disabled={!url}
            onClick={() => {
              onSelect(linkKey, unit);
              setOpen(false);
            }}
            className="flex w-full flex-col items-start gap-0.5 px-4 py-2.5 text-left hover:bg-alpha-40 disabled:cursor-not-allowed disabled:opacity-50 border-t border-alpha-40 first:border-t-0"
          >
            <span className="flex w-full items-center justify-between gap-2">
              <span className="text-sm font-medium text-content-1000">
                {unit.label}
              </span>
              {isCurrent ? (
                <HugeiconsIcon
                  icon={Tick02Icon}
                  size={18}
                  className="text-content-1000 shrink-0"
                />
              ) : null}
            </span>
            <span className="text-xs font-medium text-content-300 break-all">
              {url ? url : "No link available for this unit"}
            </span>
          </button>
        );
      })}
    </>
  );
};

export const LinkUnitResolver = ({
  linkKeys,
  variables,
  units,
  selections,
  onSelect
}: LinkUnitResolverProps) => {
  if (linkKeys.length === 0) return null;

  return (
    <div className="space-y-2 rounded-lg border border-alpha-100 bg-background-900 p-3">
      <div className="flex items-center gap-2 text-sm font-bold text-content-1000">
        <HugeiconsIcon
          icon={Alert02Icon}
          size={16}
          className="text-warning-500"
        />
        Choose a unit for each link
      </div>
      {linkKeys.map((key) => {
        const selectedUnit = selections[key];
        const label = labelFor(variables, key);
        return (
          <div key={key} className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-content-700">
              {label}
            </span>
            <Popover placement="bottom-end">
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex max-w-[60%] items-center gap-2 rounded-lg border border-alpha-100 bg-white px-3 py-1.5 text-sm font-medium text-content-1000 hover:bg-alpha-40 transition-colors"
                >
                  <span className="truncate">
                    {selectedUnit ? selectedUnit.label : "Select unit"}
                  </span>
                  <HugeiconsIcon
                    icon={ArrowDown01Icon}
                    size={16}
                    className="shrink-0 text-content-300"
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent className="z-[70] w-72 max-h-[320px] overflow-y-auto rounded-lg bg-white shadow-xl ring-1 ring-alpha-100 overflow-clip">
                <UnitPickerMenu
                  linkKey={key}
                  units={units}
                  selected={selectedUnit}
                  onSelect={onSelect}
                />
              </PopoverContent>
            </Popover>
          </div>
        );
      })}
    </div>
  );
};
